/* Slideshow — <mw-slideshow> for sections/slideshow.liquid.

   Fade: the slides share one grid cell and cross-fade (CSS). Slide: the track stays a native
   scroll-snap row, so swiping and the next-slide preview come from the browser; this file
   scrolls it and reads where it settled. Without JS the row simply scrolls.

   Autoplay stops for hover (optional), keyboard focus, off-screen, hidden tabs and while a slide
   is selected in the theme editor. The pause/play button overrides a focus hold (WCAG 2.2.2).
   Reduced motion: no autoplay until the visitor presses play, no smooth scrolling, no video.

   Event: `mw:slideshow:change` (bubbles) with detail { index, scheme } after every change. */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const mobile = window.matchMedia('(max-width: 767px)');
const SWIPE_MIN = 40; // px of horizontal travel before a swipe counts (fade mode)
const SETTLE_MS = 120; // quiet time after the last scroll event (slide mode)
const RESUME_MIN = 300; // shortest remaining delay after a pause, so a resume never jumps at once

class MwSlideshow extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.slides = [...this.querySelectorAll('[data-slide]')];
    if (!this.track || this.slides.length === 0) return;

    this.dots = [...this.querySelectorAll('[data-go]')];
    this.controls = this.querySelector('[data-controls]');
    this.toggle = this.querySelector('[data-toggle]');
    this.isSlide = this.dataset.transition === 'slide';
    this.rtl = getComputedStyle(this).direction === 'rtl';
    this.delay = Number(this.dataset.autoplay) * 1000 || 0;
    this.remaining = this.delay;
    this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
    this.inView = false;
    this.holds = new Set(['offscreen']);
    this.playing = this.delay > 0 && this.slides.length > 1 && !reducedMotion.matches;

    this.abort = new AbortController();
    const on = (target, type, handler, options = {}) =>
      target.addEventListener(type, handler, { ...options, signal: this.abort.signal });

    this.dots.forEach((dot) => on(dot, 'click', () => this.go(Number(dot.dataset.go))));
    this.querySelectorAll('[data-prev]').forEach((button) => on(button, 'click', () => this.go(this.index - 1)));
    this.querySelectorAll('[data-next]').forEach((button) => on(button, 'click', () => this.go(this.index + 1)));
    if (this.toggle) on(this.toggle, 'click', () => this.setPlaying(!this.playing));
    on(this, 'keydown', (event) => this.onKey(event));

    if (this.hasAttribute('data-hover-pause')) {
      on(this, 'pointerenter', (event) => event.pointerType === 'mouse' && this.hold('hover', true));
      on(this, 'pointerleave', (event) => event.pointerType === 'mouse' && this.hold('hover', false));
    }
    // Keyboard focus holds autoplay; a mouse click on an arrow does not.
    on(this, 'focusin', (event) => this.hold('focus', event.target.matches(':focus-visible')));
    on(this, 'focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.hold('focus', false);
    });
    on(document, 'visibilitychange', () => this.hold('hidden', document.hidden));
    on(document, 'shopify:block:select', (event) => this.onEditor(event, true));
    on(document, 'shopify:block:deselect', (event) => this.onEditor(event, false));
    on(reducedMotion, 'change', () => {
      if (reducedMotion.matches) this.setPlaying(false);
      this.syncVideos();
    });
    on(mobile, 'change', () => this.syncVideos());

    if (this.isSlide) {
      on(this.track, 'scroll', () => this.onScroll(), { passive: true });
      this.resizeObserver = new ResizeObserver(() => this.scrollToIndex('auto'));
      this.resizeObserver.observe(this.track);
    } else {
      this.bindSwipe(on);
    }
    this.bindTouchHold(on);

    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.inView = entry.isIntersecting && entry.intersectionRatio >= 0.25;
        this.hold('offscreen', !this.inView);
        if (this.inView) this.show();
        this.syncVideos();
      },
      { threshold: [0, 0.25] }
    );
    this.observer.observe(this);

    if (document.hidden) this.holds.add('hidden');
    this.setAttribute('data-ready', '');
    this.apply();
    this.sync();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
    clearTimeout(this.timer);
    clearTimeout(this.settleTimer);
    clearTimeout(this.touchTimer);
    this.timer = 0;
  }

  /* ---------- Navigation ---------- */

  go(target, { instant = false } = {}) {
    const count = this.slides.length;
    const next = ((target % count) + count) % count;
    const changed = next !== this.index;
    if (changed) {
      this.index = next;
      this.apply();
    }
    if (this.isSlide) this.scrollToIndex(instant || reducedMotion.matches ? 'auto' : 'smooth');
    else this.show();
    // Same slide: keep the running interval so the ring and the timer stay in step.
    if (changed) this.restart();
  }

  apply() {
    // Only the arriving slide is reset: it replays its entrance. A slide that leaves keeps
    // `is-shown`, so its words ride out with the media instead of blinking off first — measured
    // on Helix 20.09: at a slide change only the incoming slide's items sit at opacity 0.
    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.classList.toggle('is-active', active);
      if (active) slide.classList.remove('is-shown');
      slide.inert = !active;
    });
    this.dots.forEach((dot, i) => {
      const active = i === this.index;
      dot.classList.toggle('is-active', active);
      if (active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
    this.style.setProperty('--slideshow-index', this.index);

    const scheme = this.slides[this.index].dataset.scheme;
    if (this.controls && scheme) {
      [...this.controls.classList].filter((name) => name.startsWith('color-')).forEach((name) => this.controls.classList.remove(name));
      this.controls.classList.add(`color-${scheme}`);
    }

    // Warm the next slide so it is decoded before it arrives.
    const next = this.slides[(this.index + 1) % this.slides.length];
    next.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      img.loading = 'eager';
    });

    this.syncVideos();

    // Flush the reset, then start the entrance in the same turn: the words are on their way
    // before the slide has finished travelling, like Helix (first frame ~150 ms after the
    // change). Waiting for the scroll to settle left the new slide empty for up to a second.
    void this.slides[this.index].offsetWidth;
    this.show();

    this.dispatchEvent(new CustomEvent('mw:slideshow:change', { bubbles: true, detail: { index: this.index, scheme } }));
  }

  // The active slide's content enters as soon as the slideshow is in view. Called from apply()
  // on every change; the scroll-settle and intersection paths are the fallbacks.
  show() {
    if (this.inView) this.slides[this.index].classList.add('is-shown');
  }

  /* ---------- Slide mode: native scroll ---------- */

  edge(rect) {
    return this.rtl ? rect.right : rect.left;
  }

  scrollToIndex(behavior) {
    const delta = this.edge(this.slides[this.index].getBoundingClientRect()) - this.edge(this.track.getBoundingClientRect());
    if (Math.abs(delta) < 1) {
      this.show();
      return;
    }
    this.track.scrollBy({ left: delta, behavior });
  }

  onScroll() {
    clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(() => this.settle(), SETTLE_MS);
  }

  settle() {
    const origin = this.edge(this.track.getBoundingClientRect());
    let nearest = this.index;
    let best = Infinity;
    this.slides.forEach((slide, i) => {
      const distance = Math.abs(this.edge(slide.getBoundingClientRect()) - origin);
      if (distance < best) {
        best = distance;
        nearest = i;
      }
    });
    if (nearest !== this.index) {
      this.index = nearest;
      this.apply();
      this.restart();
    }
    this.show();
  }

  /* ---------- Fade mode: swipe ---------- */

  /* Swipe in fade mode, where the track is not a native scroller.
     Touch events, not pointer events: the browser sends `pointercancel` (never `pointerup`) the
     moment it decides the drag might pan the page, which it does on every horizontal drag unless
     the CSS says `touch-action: pan-y`. Measured 20.09 on the dev theme: a pointer-only handler
     never saw a swipe finish on a phone. `touchend` always arrives, and the gesture only counts
     when the horizontal travel clearly beats the vertical one, so a page scroll never flips a
     slide. Pointer events still serve pen and mouse drags. */
  bindSwipe(on) {
    let start = null;
    const finish = (x, y) => {
      if (!start) return;
      const dx = x - start.x;
      const dy = y - start.y;
      start = null;
      if (Math.abs(dx) < SWIPE_MIN || Math.abs(dx) < Math.abs(dy) * 1.2) return;
      const forward = this.rtl ? dx > 0 : dx < 0;
      this.go(this.index + (forward ? 1 : -1));
    };

    on(this.track, 'touchstart', (event) => {
      start = event.touches.length === 1 ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
    }, { passive: true });
    on(this.track, 'touchend', (event) => {
      const touch = event.changedTouches[0];
      if (touch) finish(touch.clientX, touch.clientY);
      else start = null;
    }, { passive: true });
    on(this.track, 'touchcancel', () => {
      start = null;
    }, { passive: true });

    on(this.track, 'pointerdown', (event) => {
      start = event.pointerType === 'pen' && event.isPrimary ? { x: event.clientX, y: event.clientY } : start;
    });
    on(this.track, 'pointerup', (event) => {
      if (event.pointerType === 'pen') finish(event.clientX, event.clientY);
    });
  }

  /* A finger on the slideshow holds autoplay, in both modes: measured 20.09, a slide could change
     under a finger that was resting on it, which moves the tap target away mid-tap. The hold is
     released a moment after the finger leaves so a flick's momentum scroll can settle first. */
  bindTouchHold(on) {
    const release = () => {
      clearTimeout(this.touchTimer);
      this.touchTimer = setTimeout(() => this.hold('touch', false), RESUME_MIN);
    };
    on(this.track, 'touchstart', () => {
      clearTimeout(this.touchTimer);
      this.hold('touch', true);
    }, { passive: true });
    on(this.track, 'touchend', release, { passive: true });
    on(this.track, 'touchcancel', release, { passive: true });
  }

  onKey(event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    if (!event.target.closest('[data-controls]')) return;
    event.preventDefault();
    const forward = (event.key === 'ArrowRight') !== this.rtl;
    this.go(this.index + (forward ? 1 : -1));
    if (event.target.matches('[data-go]')) this.dots[this.index]?.focus();
  }

  /* ---------- Autoplay ---------- */

  get running() {
    return this.playing && this.holds.size === 0;
  }

  hold(reason, active) {
    if (active) this.holds.add(reason);
    else this.holds.delete(reason);
    this.sync();
  }

  setPlaying(playing) {
    this.playing = playing && this.delay > 0 && this.slides.length > 1;
    if (this.playing) {
      // An explicit «play» wins over the focus and hover holds.
      this.holds.delete('focus');
      this.holds.delete('hover');
      if (!this.remaining) this.remaining = this.delay;
    }
    this.sync();
  }

  sync() {
    const running = this.running;
    this.toggleAttribute('data-playing', this.playing);
    this.toggleAttribute('data-paused', !running);
    this.track.setAttribute('aria-live', this.playing ? 'off' : 'polite');
    if (this.toggle) {
      this.toggle.setAttribute('aria-label', this.playing ? this.toggle.dataset.labelPause : this.toggle.dataset.labelPlay);
    }
    if (running && !this.timer) this.startTimer();
    else if (!running && this.timer) this.stopTimer();
  }

  startTimer() {
    this.startedAt = performance.now();
    this.timer = setTimeout(() => {
      this.timer = 0;
      this.go(this.index + 1);
    }, this.remaining);
  }

  stopTimer() {
    clearTimeout(this.timer);
    this.timer = 0;
    const left = this.remaining - (performance.now() - this.startedAt);
    this.remaining = Math.min(this.delay, Math.max(left, RESUME_MIN));
  }

  // A new slide gets the full interval (the progress ring restarts with its class).
  restart() {
    clearTimeout(this.timer);
    this.timer = 0;
    this.remaining = this.delay;
    this.sync();
  }

  /* ---------- Media and editor ---------- */

  syncVideos() {
    const allow = this.inView && !reducedMotion.matches;
    this.slides.forEach((slide, i) => {
      slide.querySelectorAll('video').forEach((video) => {
        if (allow && i === this.index && video.offsetParent !== null) {
          video.play()?.catch(() => {});
        } else {
          video.pause();
        }
      });
    });
  }

  onEditor(event, selected) {
    const slide = event.target.closest?.('[data-slide]');
    if (!slide || !this.slides.includes(slide)) return;
    if (selected) this.go(this.slides.indexOf(slide), { instant: true });
    this.hold('editor', selected);
  }
}

if (!customElements.get('mw-slideshow')) customElements.define('mw-slideshow', MwSlideshow);
